import React, { createContext, useContext, useReducer, useEffect } from "react";
import { apiClient } from "@/services/api";

interface User {
  id: number | string;
  username: string;
  email?: string;
  phone?: string;
  role?: string;
  display_name?: string;
  organization_name?: string;
  organization_id?: number;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
}

type AuthAction =
  | { type: "SET_LOADING"; payload: boolean }
  | { type: "SET_USER"; payload: User | null }
  | { type: "LOGOUT" };

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  loading: true,
};

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case "SET_LOADING":
      return { ...state, loading: action.payload };
    case "SET_USER":
      // 保存用户信息到localStorage
      if (action.payload) {
        localStorage.setItem("currentUser", JSON.stringify(action.payload));
      } else {
        localStorage.removeItem("currentUser");
      }
      return {
        ...state,
        user: action.payload,
        isAuthenticated: !!action.payload,
        loading: false,
      };
    case "LOGOUT":
      // 清除localStorage中的用户信息
      localStorage.removeItem("currentUser");
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        loading: false,
      };
    default:
      return state;
  }
};

interface AuthContextType extends AuthState {
  login: (credentials: { username: string; password: string }) => Promise<void>;
  loginWithSms: (phone: string, code: string) => Promise<void>;
  logout: () => void;
  checkAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // 初始化时从localStorage加载用户信息
  const getInitialState = (): AuthState => {
    try {
      const storedUser = localStorage.getItem("currentUser");
      const authToken = localStorage.getItem("auth_token");

      // 必须同时有用户信息和认证token才认为是已登录状态
      if (storedUser && authToken) {
        const user = JSON.parse(storedUser);
        return {
          user,
          isAuthenticated: true,
          loading: false,
        };
      } else if (storedUser && !authToken) {
        // 有用户信息但没有token，清理用户信息
        localStorage.removeItem("currentUser");
      }
    } catch (error) {
      console.error("Error loading user from localStorage:", error);
      // 清理可能损坏的数据
      localStorage.removeItem("currentUser");
    }
    return initialState;
  };

  const [state, dispatch] = useReducer(authReducer, getInitialState());

  const checkAuth = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true })

      // 检查本地是否有token和用户信息
      const token = localStorage.getItem('auth_token')
      const storedUser = localStorage.getItem('currentUser')

      if (!token) {
        // 没有token，直接设置为未认证状态
        dispatch({ type: "SET_USER", payload: null });
        return
      }

      // 设置超时，避免无限等待
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('认证检查超时')), 5000) // 5秒超时
      })

      const authPromise = apiClient.auth.getSession()

      const response = await Promise.race([authPromise, timeoutPromise])

      if (response && (response as any).success && (response as any).data) {
        dispatch({ type: 'SET_USER', payload: (response as any).data })
      } else {
        dispatch({ type: "SET_USER", payload: null });
      }
    } catch (error: any) {
      console.error('Auth check failed:', error)

      // 如果是超时或网络错误，优先使用本地存储的用户信息
      if (error.message?.includes('超时') || error.message?.includes('网络') || error.message?.includes('连接')) {
        console.log('网络连接问题，使用本地存储的用户信息')
        try {
          const storedUser = localStorage.getItem('currentUser')
          const token = localStorage.getItem('auth_token')
          if (storedUser && token) {
            const user = JSON.parse(storedUser)
            dispatch({ type: 'SET_USER', payload: user })
            return
          }
        } catch (e) {
          console.error('解析本地用户信息失败:', e)
        }
      }

      // 如果是403权限错误或401未授权错误（含会话被挤下线），清除token
      if (error.message?.includes('权限不足') || error.message?.includes('未授权') || error.message?.includes('会话')) {
        apiClient.clearToken()
      }

      dispatch({ type: 'SET_USER', payload: null })
    }
  };

  const login = async (credentials: { username: string; password: string }) => {
    try {
      const response: any = await apiClient.auth.login(credentials);
      if (response.success && response.data?.access_token) {
        apiClient.setToken(response.data.access_token);
        // 直接缓存用户信息，避免首次 checkAuth 401 造成误判
        if (response.data) {
          const user = {
            id: response.data.id,
            username: response.data.username,
            display_name: response.data.display_name,
            role: response.data.role,
            organization_name: response.data.organization_name,
            organization_id: response.data.organization_id,
          } as User;
          localStorage.setItem('currentUser', JSON.stringify(user));
          dispatch({ type: 'SET_USER', payload: user });
        }
        // 异步刷新一次会话信息
        setTimeout(() => checkAuth(), 0);
      } else {
        throw new Error(response.message || "登录失败");
      }
    } catch (error) {
      throw error;
    }
  };

  const loginWithSms = async (phone: string, code: string) => {
    try {
      const response = await apiClient.auth.loginWithSms(phone, code);
      if (response.success && response.data?.access_token) {
        apiClient.setToken(response.data.access_token);
        await checkAuth();
      } else {
        throw new Error(response.message || "登录失败");
      }
    } catch (error) {
      throw error;
    }
  };

  const logout = () => {
    apiClient.clearToken();
    dispatch({ type: "LOGOUT" });
  };

  useEffect(() => {
    // 只有在有token的情况下才进行认证检查
    const token = localStorage.getItem('auth_token')
    if (token) {
      checkAuth()
    } else {
      // 没有token，直接设置为未认证状态
      dispatch({ type: 'SET_LOADING', payload: false })
    }
  }, [])

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        loginWithSms,
        logout,
        checkAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
